import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Card, Collapse, Spinner, Button } from 'reactstrap';
import { useSelector, useDispatch } from "react-redux";
import Slider from "react-slick";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

import { Assessment } from "./Assessment";
import { YourReport } from "./YourReport";
import icon_tt from '../../../assets/img/ThinkingTalent.svg';
import icon_mp from '../../../assets/img/MindPattern.svg';
import icon_quest from '../../../assets/img/Questionnaire.svg';
import { QuestionnaireProgress } from "../../pages/home/questionnaires/QuestionnaireProgress";
import { userActions } from "../../../core/users";

export const BoostYourGrowth = () => {
    const dispatch = useDispatch();
    const sliderRef = useRef(null);
    const user = useSelector(state => state.user);
    const questionnaire_resume = useSelector(state => state.questionnaire_resume);
    const systemCheck = useSelector(state => state.systemCheck);
    const mind_patterns_store_mp = useSelector(state => state.mind_patterns_store_mp);

    const account = systemCheck?.data?.data;
    const [showQuestionnaires, setShowQuestionnaires] = useState(false);
    const uniqueQuestionnaire = questionnaire_resume?.data?.length === 1;
    const animationTime = useRef(0.4);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isExpanding, setIsExpanding] = useState(false);
    const [isCollapsing, setIsCollapsing] = useState(false);
    const [contentHeight, setContentHeight] = useState("1.5em");
    const contentRef = useRef(null);

    const toggleExpanded = () => {
        if (isExpanded) {
            setIsCollapsing(true);
        } else {
            setIsExpanding(true);
        }
        setIsExpanded(prev => !prev);
    };

    useEffect(() => {
        if (isExpanded) {
            // Mide la altura del contenido completo cuando está expandido
            const height = contentRef.current.scrollHeight;
            setContentHeight(`${height}px`);
        } else {
            // Vuelve a la altura contraída
            setContentHeight("1.5em");
        }
    }, [isExpanded]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (isExpanding) {
                setIsExpanding(false);
            } else if (isCollapsing) {
                setIsCollapsing(false);
            }
        }, animationTime.current * 1000);

        return () => clearTimeout(timeout); // Limpia el timeout si el componente se desmonta
    }, [isExpanding, isCollapsing]);

    const toggleShowQuestionnaires = useCallback(() => {
        setShowQuestionnaires(prev => !prev);
    }, []);

    const allQuestionnairesCompleted = useMemo(() => {
        return questionnaire_resume.data?.every(questionnaire => questionnaire.submitted);
    }, [questionnaire_resume]);

    useEffect(() => {
        if (allQuestionnairesCompleted && !!user.talents?.length && !!user.mind_pattern && !user?.can_download_pdf) {
            dispatch(userActions.currentUser());
        }
    }, [allQuestionnairesCompleted]);

    const assessments = useMemo(() => {
        return [
            { title: 'Thinking Talents', icon: icon_tt, completed: !!user.talents?.length, link: '/thinking-talents' },
            { title: 'Mind Patterns', icon: icon_mp, completed: !!user.mind_pattern, link: '/mind-patterns', loading: mind_patterns_store_mp?.isPending },
            {
                title: uniqueQuestionnaire ? questionnaire_resume.data[0].name : 'Questionnaires',
                icon: icon_quest,
                completed: allQuestionnairesCompleted,
                questionnaires: questionnaire_resume?.data,
                link: uniqueQuestionnaire ? `/questionnaire/${questionnaire_resume.data[0].id}` : '',
                loading: !questionnaire_resume?.data?.length && questionnaire_resume.isPending
            },
        ];
    }, [user, questionnaire_resume, uniqueQuestionnaire, allQuestionnairesCompleted]);

    const items = useMemo(() => questionnaire_resume.data.map(questionnaire => {
        return (
            <div className="px-2 w-100" key={questionnaire.id}>
                <QuestionnaireProgress questionnaire={questionnaire} />
            </div>
        );
    }), [questionnaire_resume]);

    const slideShow = (items.length && items.length < 3) ? items.length : 3;

    const sliderSettingsRef = {
        arrows: true,
        dots: false,
        infinite: true,
        slidesToShow: slideShow,
        slidesToScroll: 1,
        responsive: [
            {
                breakpoint: 575,
                settings: {
                slidesToShow: 1,
                }
            }
        ]
    };

    return (
        <Card color="ice-40" className="p-3 mb-4 border-ice-70">
            <div className="d-flex align-items-center justify-content-between">
                <h2 className="h3">{account?.assessment_title}</h2>
                <Button color="link" onClick={toggleExpanded}>
                    <FontAwesomeIcon icon={faChevronDown} flip={isExpanded ? 'vertical' : false} />
                </Button>
            </div>
            <div>
                <div className={`mb-3 assessment-presentation ${isExpanded && !isExpanding ? "content-expanded" : ""}`}>
                    <div
                        className={`collapsible-content ${isExpanded ? "expanded" : ""}`}
                        ref={contentRef}
                        style={{
                            overflow: (isExpanded && !isExpanding) ? "auto" : "hidden",
                            height: contentHeight,
                            transition: `height ${animationTime.current}s ease`,
                        }}
                    >
                        <div className="" style={{ WebkitBoxOrient: "vertical", WebkitLineClamp: isExpanded ? "none" : "1" }}>
                            <p data-renderer-start-pos="222" className="m-0 d-inline-block mr-1">Get started on your below assessments and view your results live as you complete each one.</p>
                            {!isExpanded && !isExpanding && (
                                <Button color="link" onClick={toggleExpanded} className="text-nowrap d-inline p-0 text-danger font-weight-semibold border-0" style={{marginBottom: '1px'}}>
                                    View more
                                </Button>
                            )}
                        </div>
                        {(isExpanded || isCollapsing) && (
                            <>
                                <p data-renderer-start-pos="314">Once you have completed the assessments, download your full assessment report which summarizes your results and offers insights, strategies, and tactics to grow your effectiveness.</p>
                                <ul className="ak-ul" data-indent-level="1">
                                    <li>
                                        <p data-renderer-start-pos="500">Thinking Talents:&nbsp;What lights you up</p>
                                    </li>
                                    <li>
                                        <p data-renderer-start-pos="540">Mind Patterns:&nbsp;How you communicate and learn</p>
                                    </li>
                                    <li>
                                        <p data-renderer-start-pos="588" className="d-inline-block mb-0 mr-1">Leadership insights: conflict management, leadership, decision-making and problem-solving styles.</p>
                                        <Button color="link" onClick={toggleExpanded} className="text-nowrap d-inline p-0 text-body font-weight-semibold border-0" style={{marginBottom: '1px'}}>
                                            View less
                                        </Button>
                                    </li>
                                </ul>
                            </>
                        )}
                    </div>
                </div>

                <div className="grid-assessment w-100 position-relative">
                    <div className="grid-content grid-3">
                        {assessments.map((assessment, index) => (
                            <div className="mb-3" key={index}>
                                {!assessment.loading ? (
                                    <Assessment
                                        title={assessment.title}
                                        icon={assessment.icon}
                                        completed={assessment.completed}
                                        link={assessment.link}
                                        questionnaires={assessment.questionnaires}
                                        loading={assessment.loading}
                                        toggle={toggleShowQuestionnaires}
                                    />
                                ) : (
                                    <div className="d-flex justify-content-center align-content-end mt-5">
                                        <Spinner color="primary" style={{ height: '3rem', width: '3rem' }} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <Collapse isOpen={showQuestionnaires} className="collapse-down w-100">
                        <Card className="pt-4 w-100 border-info mb-3">
                            <div className="d-flex align-items-center mb-3 px-4">
                                <img src={icon_quest} alt='Questionnaires' width="40" height="auto" />
                                <h2 className="mb-0 ml-3"> Questionnaires </h2>
                            </div>

                            {items.length > 0 && (
                                <div className="w-100 px-sm-1">
                                    <Slider className={`slide-questionnaires mb-5 ${items.length < 3 ? 'mb-sm-3' : 'mb-sm-5'}`} ref={sliderRef} {...sliderSettingsRef}>
                                        {items}
                                    </Slider>
                                </div>
                            )}
                        </Card>
                    </Collapse>
                </div>
                <YourReport />
            </div>
        </Card>
    );
};